import { addDays, addMonths, daysInMonth, weekday, type LocalDate } from '../dates';
import type { DateRange } from '../ledger/counting';
import { daysBetween } from '../spending/range';

/** Spec §6: if payday falls on a weekend (or holiday), pay the business day before (default) or after. */
export type WeekendRule = 'before' | 'after';
export type IncomeKind = 'salary' | 'bonus' | 'other';

/**
 * Stored as JSON in income_sources.schedule.
 * - semi_monthly / monthly: days of the month, 1-31; a day past the month's end means its last day
 *   (31 = "last day", so 30 in February is Feb 28 or 29).
 * - biweekly / weekly: every 14 / 7 days from `anchor` (a real payday).
 * - yearly_dates: 'MM-DD' dates every year (bonuses: ['12-15'] for 13th month pay). '02-29' is Feb 28 in other years.
 *   v0.5 `withPayday`: the bonus arrives with a salary's payday instead ("with the 15th payday in
 *   December"): each date keeps only its month, and the day and weekend move are that salary
 *   slot's (resolveTiedSchedules), so it always lands in the same cutoff as that payday.
 * - every_n_months: every `n` months from `anchor`, always counted from the anchor so days never drift.
 */
export type IncomeSchedule =
  | { kind: 'semi_monthly'; days: [number, number] }
  | { kind: 'monthly'; day: number }
  | { kind: 'biweekly'; anchor: LocalDate }
  | { kind: 'weekly'; anchor: LocalDate }
  | { kind: 'yearly_dates'; dates: string[]; withPayday?: PaydayTie }
  | { kind: 'every_n_months'; n: number; anchor: LocalDate };

/** v0.5: a bonus tied to a salary source's payday slot (0 = first day of the month, 1 = second). */
export interface PaydayTie {
  sourceId: string;
  slot: number;
}

export type ScheduleKind = IncomeSchedule['kind'];

export interface IncomeSourceLike {
  id: string;
  name: string;
  kind: IncomeKind;
  schedule: IncomeSchedule;
  /** Minor units, one per slot (slotCount): per cutoff for semi-monthly, per date for yearly_dates. */
  defaultAmounts: number[];
  weekendRule: WeekendRule;
  /** No paydays are generated before this day. */
  startDate: LocalDate;
  /**
   * v0.7.4 "Last payday on or before": no paydays are generated after this day (the schedule's
   * own date, like startDate). Null or absent = no end.
   */
  endDate?: LocalDate | null;
  categoryId: string | null;
  /** v0.5: the account its paydays land in (null or absent = Budget's "Receive into" default). */
  accountId?: string | null;
  deletedAt: string | null;
}

/** One scheduled payday of a source, before anything the user did to it. */
export interface Occurrence {
  sourceId: string;
  /** The schedule's own date (the key for its payday row; weekend rule not applied). */
  nominalDate: LocalDate;
  /** The day the money is expected, after the weekend/holiday rule. */
  date: LocalDate;
  /** The schedule slot (first one when two slots clamp to the same day). */
  slot: number;
  /** The slot's default amount, or the sum when two slots clamp to the same day. */
  defaultAmountMinor: number;
}

export const DEFAULT_SALARY_SCHEDULE: IncomeSchedule = { kind: 'semi_monthly', days: [15, 31] };
/** v0.2 moves paydays off weekends only; a holiday list can be passed in later (Ivan, 2026-11-02). */
export const NO_HOLIDAYS: ReadonlySet<LocalDate> = new Set<LocalDate>();
const MMDD_RE = /^(\d{2})-(\d{2})$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n: number) => String(n).padStart(2, '0');
const byText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

export function slotCount(s: IncomeSchedule): number {
  switch (s.kind) {
    case 'semi_monthly':
      return 2;
    case 'yearly_dates':
      return s.dates.length;
    default:
      return 1;
  }
}

/** Day `day` of a month, clamped to its last day (the 31st in a 30-day month is the 30th). */
export function clampDay(year: number, month1to12: number, day: number): LocalDate {
  const d = Math.min(day, daysInMonth(year, month1to12));
  return `${String(year).padStart(4, '0')}-${pad(month1to12)}-${pad(d)}`;
}

export function isBusinessDay(
  d: LocalDate,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): boolean {
  const w = weekday(d);
  return w !== 0 && w !== 6 && !holidays.has(d);
}

/** Moves a payday off weekends and holidays: to the business day before (default) or after. */
export function adjustPayday(
  d: LocalDate,
  rule: WeekendRule,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): LocalDate {
  const step = rule === 'before' ? -1 : 1;
  let out = d;
  for (let i = 0; i < 14 && !isBusinessDay(out, holidays); i++) out = addDays(out, step);
  return out;
}

function eachMonth(from: LocalDate, to: LocalDate): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  const endY = Number(to.slice(0, 4));
  const endM = Number(to.slice(5, 7));
  while (y < endY || (y === endY && m <= endM)) {
    out.push({ year: y, month: m });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/**
 * Schedule dates (weekend rule not applied) with from <= date <= to, ascending. Two slots that
 * clamp to the same day (the 29th and 31st in February, yearly 02-28 and 02-29) share one date and
 * keep both slots, so neither payday is lost; their amounts are added together.
 */
export function nominalDatesBetween(
  s: IncomeSchedule,
  from: LocalDate,
  to: LocalDate,
): { date: LocalDate; slots: number[] }[] {
  const out: { date: LocalDate; slots: number[] }[] = [];
  const push = (date: LocalDate, slot: number) => {
    if (date < from || date > to) return;
    const same = out.find((o) => o.date === date);
    if (!same) out.push({ date, slots: [slot] });
    else if (!same.slots.includes(slot)) same.slots.push(slot);
  };
  switch (s.kind) {
    case 'semi_monthly':
    case 'monthly': {
      const days = s.kind === 'monthly' ? [s.day] : s.days;
      for (const { year, month } of eachMonth(from, to)) {
        days.forEach((d, slot) => push(clampDay(year, month, d), slot));
      }
      break;
    }
    case 'biweekly':
    case 'weekly': {
      const step = s.kind === 'weekly' ? 7 : 14;
      let k = Math.ceil(daysBetween(s.anchor, from) / step);
      for (let d = addDays(s.anchor, k * step); d <= to; k++, d = addDays(s.anchor, k * step)) {
        push(d, 0);
      }
      break;
    }
    case 'yearly_dates': {
      for (let y = Number(from.slice(0, 4)); y <= Number(to.slice(0, 4)); y++) {
        s.dates.forEach((md, slot) => {
          const m = MMDD_RE.exec(md);
          if (m) push(clampDay(y, Number(m[1]), Number(m[2])), slot);
        });
      }
      break;
    }
    case 'every_n_months': {
      const monthsToFrom =
        (Number(from.slice(0, 4)) - Number(s.anchor.slice(0, 4))) * 12 +
        (Number(from.slice(5, 7)) - Number(s.anchor.slice(5, 7)));
      // Can be negative (a range before the anchor): the schedule runs both ways from it.
      let k = Math.floor(monthsToFrom / s.n) - 1;
      for (let d = addMonths(s.anchor, k * s.n); d <= to; k++, d = addMonths(s.anchor, k * s.n)) {
        push(d, 0);
      }
      break;
    }
  }
  return out.sort((a, b) => byText(a.date, b.date));
}

/** How far the weekend/holiday rule can move a payday, so range queries look a little wider. */
const ADJUST_MARGIN_DAYS = 10;

/**
 * Paydays of a live source whose expected (adjusted) date is in `range`, ascending.
 * Nothing before the source's startDate or after its endDate; skips, confirmations and edits are applied later (paydays.ts).
 */
export function occurrencesInRange(
  source: IncomeSourceLike,
  range: DateRange,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): Occurrence[] {
  if (source.deletedAt) return [];
  return nominalDatesBetween(
    source.schedule,
    addDays(range.start, -ADJUST_MARGIN_DAYS),
    addDays(range.end, ADJUST_MARGIN_DAYS),
  )
    .filter(
      (n) => n.date >= source.startDate && (source.endDate == null || n.date <= source.endDate),
    )
    .map((n) => ({
      sourceId: source.id,
      nominalDate: n.date,
      date: adjustPayday(n.date, source.weekendRule, holidays),
      slot: n.slots[0]!,
      defaultAmountMinor: n.slots.reduce(
        (sum, slot) => sum + (source.defaultAmounts[slot] ?? source.defaultAmounts[0] ?? 0),
        0,
      ),
    }))
    .filter((o) => o.date >= range.start && o.date <= range.end)
    .sort((a, b) => byText(a.date, b.date));
}

/** The day of the month a salary slot pays (semi-monthly: days[slot]; monthly: its day), or null. */
function slotDay(s: IncomeSchedule, slot: number): number | null {
  if (s.kind === 'semi_monthly') return s.days[slot] ?? null;
  if (s.kind === 'monthly') return slot === 0 ? s.day : null;
  return null;
}

/**
 * v0.5: bonuses tied to a salary payday (yearly_dates withPayday) get that payday's day of the
 * month and the salary's weekend rule, so a 13th month pay "with the 15th payday in December"
 * moves with that payday (Friday the 13th when the 15th is a Sunday) and stays in its cutoff.
 * A tie to a salary that is gone or has no such slot keeps the bonus's own fixed dates.
 */
export function resolveTiedSchedules<T extends IncomeSourceLike>(sources: readonly T[]): T[] {
  const byId = new Map(sources.map((s) => [s.id, s]));
  return sources.map((s) => {
    if (s.schedule.kind !== 'yearly_dates' || !s.schedule.withPayday) return s;
    const salary = byId.get(s.schedule.withPayday.sourceId);
    if (!salary || salary.deletedAt) return s;
    const day = slotDay(salary.schedule, s.schedule.withPayday.slot);
    if (day === null) return s;
    // Day 31 is kept as is: nominalDatesBetween clamps it to the month's last day.
    const dates = s.schedule.dates.map((md) => `${md.slice(0, 2)}-${pad(day)}`);
    return {
      ...s,
      // Not de-duplicated: one amount per date slot (two slots on one day add up).
      schedule: { kind: 'yearly_dates', dates },
      weekendRule: salary.weekendRule,
    };
  });
}

export type IncomeSourceError =
  | 'name_required'
  | 'name_too_long'
  | 'bad_schedule'
  | 'amounts_mismatch'
  | 'bad_amount'
  | 'bad_date'
  | 'end_before_start';

export const MAX_SOURCE_NAME = 40;
export const MAX_INCOME_MINOR = 1_000_000_000; // ₱10,000,000.00

function validDay(d: number): boolean {
  return Number.isInteger(d) && d >= 1 && d <= 31;
}

export function isValidLocalDate(d: string): boolean {
  if (!DATE_RE.test(d)) return false;
  const m = Number(d.slice(5, 7));
  const day = Number(d.slice(8, 10));
  return m >= 1 && m <= 12 && day >= 1 && day <= daysInMonth(Number(d.slice(0, 4)), m);
}

export function validateSchedule(s: IncomeSchedule): boolean {
  switch (s.kind) {
    case 'semi_monthly':
      return s.days.length === 2 && s.days.every(validDay) && s.days[0] < s.days[1];
    case 'monthly':
      return validDay(s.day);
    case 'biweekly':
    case 'weekly':
      return isValidLocalDate(s.anchor);
    case 'yearly_dates':
      if (
        s.withPayday !== undefined &&
        !(
          typeof s.withPayday.sourceId === 'string' &&
          s.withPayday.sourceId !== '' &&
          (s.withPayday.slot === 0 || s.withPayday.slot === 1)
        )
      )
        return false;
      return (
        s.dates.length >= 1 &&
        s.dates.length <= 12 &&
        new Set(s.dates).size === s.dates.length &&
        s.dates.every((md) => {
          const m = MMDD_RE.exec(md);
          if (!m) return false;
          const month = Number(m[1]);
          const day = Number(m[2]);
          // 2028 is a leap year, so '02-29' is allowed.
          return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(2028, month);
        })
      );
    case 'every_n_months':
      return Number.isInteger(s.n) && s.n >= 1 && s.n <= 24 && isValidLocalDate(s.anchor);
    default:
      return false;
  }
}

export interface IncomeSourceInput {
  name: string;
  kind: 'salary' | 'bonus';
  schedule: IncomeSchedule;
  defaultAmounts: number[];
  weekendRule: WeekendRule;
  startDate: LocalDate;
  /** v0.7.4: the last payday on or before this day (null = no end). Left out = unchanged. */
  endDate?: LocalDate | null;
  categoryId?: string | null;
  /** v0.5: the account its paydays land in (null = Budget default). Left out = unchanged. */
  accountId?: string | null;
}

export function validateIncomeSource(input: IncomeSourceInput): IncomeSourceError | null {
  const name = input.name.trim();
  if (name === '') return 'name_required';
  if (name.length > MAX_SOURCE_NAME) return 'name_too_long';
  if (!validateSchedule(input.schedule)) return 'bad_schedule';
  if (input.defaultAmounts.length !== slotCount(input.schedule)) return 'amounts_mismatch';
  if (input.defaultAmounts.some((a) => !Number.isInteger(a) || a <= 0 || a > MAX_INCOME_MINOR)) {
    return 'bad_amount';
  }
  if (!isValidLocalDate(input.startDate)) return 'bad_date';
  if (input.endDate != null) {
    if (!isValidLocalDate(input.endDate)) return 'bad_date';
    if (input.endDate < input.startDate) return 'end_before_start';
  }
  return null;
}
